import { Boundaries } from "@planzone/core";
import { Identifiable, Resource, resourcesListingFilter } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { reduceResource } from "../../common/resource";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  resources: [],
  filter: resourcesListingFilter,
  totalPages: 0,
};

function handleRequest(
  state: State,
  { filter }: Boundaries["retrievePlanzoneResourcesListing"]["request"],
): State {
  return filter.page === 0
    ? {
        resources: [],
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
        totalPages: 0,
      }
    : {
        ...state,
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
      };
}

function handleSuccess(
  state: State,
  {
    resources,
    filter,
    totalPages,
  }: Boundaries["retrievePlanzoneResourcesListing"]["success"],
): State {
  // A late page of criteria replaced since would be appended to the new list.
  if (filter.page !== state.filter.page - 1) return state;

  return {
    ...state,
    resources:
      filter.page === 0 ? resources : [...state.resources, ...resources],
    request: setRequestStateToSuccess(),
    totalPages,
  };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

// Added and invited resources are listed first, like in retrievePlanzoneResources.
function addResources(
  state: State,
  resources: Identifiable<Resource>[],
): State {
  return { ...state, resources: [...resources, ...state.resources] };
}

function handleResourceAddition(
  state: State,
  { resource }: Boundaries["addPlanzoneMaterialResource"]["success"],
): State {
  return addResources(state, [resource]);
}

function handleProjectResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectMaterialResource"]["success"],
): State {
  return addResources(state, [projectResource.resource]);
}

function handleInvitationsSuccess(
  state: State,
  { members }: Boundaries["inviteMembers"]["success"],
): State {
  return addResources(
    state,
    members.map(({ resource }) => resource),
  );
}

function handleInvitationsFailure(
  state: State,
  { succeededInvitations }: Boundaries["inviteMembers"]["failure"],
): State {
  return addResources(
    state,
    succeededInvitations.map(({ resource }) => resource),
  );
}

function handleResourceRemoval(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return {
    ...state,
    resources: state.resources
      .filter(({ uuid }) => resource.uuid !== uuid)
      .map((res) => ({
        ...res,
        subordinates: (res.subordinates ?? []).filter(
          (s) => s.uuid !== resource.uuid,
        ),
      })),
  };
}

// The list shows all the resources, or only the deactivated or the activated ones: a resource whose state
// changes leaves the latter.
function handleResourceActivationChange(
  state: State,
  resource: Boundaries["disableResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources
      .map((res) =>
        res.uuid === resource.uuid
          ? { ...res, deactivated: resource.deactivated }
          : res,
      )
      .filter(
        ({ uuid, deactivated }) =>
          uuid !== resource.uuid ||
          ((!state.filter.inactiveOnly || deactivated) &&
            (!state.filter.activeOnly || !deactivated)),
      ),
  };
}

function handleAssignManagerToResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["assignManagerToResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? { ...res, subordinates: [...(res.subordinates ?? []), resource] }
        : res,
    ),
  };
}

function handleUnAssignManagerFromResourceSuccess(
  state: State,
  { resource, manager }: Boundaries["unAssignManagerFromResource"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: (res.subordinates ?? []).filter(
              (s) => s.uuid !== resource.uuid,
            ),
          }
        : res,
    ),
  };
}

function handleAssignEveryoneToManagerSuccess(
  state: State,
  { manager, subordinates }: Boundaries["assignEveryoneToManager"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: [...(res.subordinates ?? []), ...subordinates],
          }
        : res,
    ),
  };
}

function handleSubordinatesRemoval(
  state: State,
  { manager, subordinateUuids }: Boundaries["removeSubordinates"]["success"],
): State {
  return {
    ...state,
    resources: state.resources.map((res) =>
      res.uuid === manager.uuid
        ? {
            ...res,
            subordinates: (res.subordinates ?? []).filter(
              ({ uuid }) => !subordinateUuids.includes(uuid),
            ),
          }
        : res,
    ),
  };
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    resources: state.resources.map((resource) =>
      reduceResource(resource, event),
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrievePlanzoneResourcesListingRequested]: handleRequest,
      [events.retrievePlanzoneResourcesListingSucceeded]: handleSuccess,
      [events.retrievePlanzoneResourcesListingFailed]: handleFailure,
      [events.addPlanzoneMaterialResourceSucceeded]: handleResourceAddition,
      [events.addPlanzoneVirtualResourceSucceeded]: handleResourceAddition,
      [events.addProjectMaterialResourceSucceeded]:
        handleProjectResourceAddition,
      [events.addProjectVirtualResourceSucceeded]:
        handleProjectResourceAddition,
      [events.inviteMembersSucceeded]: handleInvitationsSuccess,
      [events.inviteMembersFailed]: handleInvitationsFailure,
      [events.removeResourceFromPlanzoneSucceeded]: handleResourceRemoval,
      [events.disableResourceSucceeded]: handleResourceActivationChange,
      [events.activateResourceSucceeded]: handleResourceActivationChange,
      [events.assignManagerToResourceSucceeded]:
        handleAssignManagerToResourceSuccess,
      [events.unAssignManagerFromResourceSucceeded]:
        handleUnAssignManagerFromResourceSuccess,
      [events.assignEveryoneToManagerSucceeded]:
        handleAssignEveryoneToManagerSuccess,
      [events.removeSubordinatesSucceeded]: handleSubordinatesRemoval,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
